import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { TILE_SIZE, tileToWorld, type DungeonLevel, type TileKind } from '@dungeon-builder/shared';

/**
 * Placeholder look per tile kind. These blocks stand in for the POLYGON art pack
 * prefabs, which are not wired up yet.
 */
const TILE_STYLE: Record<TileKind, { color: Color3; height: number }> = {
  floor: { color: new Color3(0.24, 0.22, 0.27), height: 0.25 },
  wall: { color: new Color3(0.38, 0.35, 0.42), height: 3.5 },
  door: { color: new Color3(0.55, 0.38, 0.18), height: 3.0 },
  spawn: { color: new Color3(0.25, 0.62, 0.4), height: 0.35 },
  exit: { color: new Color3(0.72, 0.55, 0.22), height: 0.35 },
  trap: { color: new Color3(0.68, 0.24, 0.28), height: 0.3 },
  chest: { color: new Color3(0.82, 0.68, 0.3), height: 1.2 },
};

/** Renders a `DungeonLevel` as untextured blocks. Disposable and rebuildable. */
export class DungeonView {
  private readonly meshes: Mesh[] = [];
  private readonly materials = new Map<TileKind, StandardMaterial>();

  constructor(private readonly scene: Scene) {}

  load(level: DungeonLevel): void {
    this.clear();

    for (const tile of level.tiles) {
      const style = TILE_STYLE[tile.kind];
      const world = tileToWorld(tile.x, tile.z);

      const mesh = MeshBuilder.CreateBox(
        `tile_${tile.kind}_${tile.x}_${tile.z}`,
        { width: TILE_SIZE * 0.98, depth: TILE_SIZE * 0.98, height: style.height },
        this.scene,
      );
      mesh.position = new Vector3(world.x, style.height / 2, world.z);
      mesh.rotation.y = (tile.rotation * Math.PI) / 180;
      mesh.material = this.materialFor(tile.kind, style.color);

      this.meshes.push(mesh);
    }
  }

  clear(): void {
    for (const mesh of this.meshes) mesh.dispose();
    this.meshes.length = 0;
  }

  dispose(): void {
    this.clear();
    for (const material of this.materials.values()) material.dispose();
    this.materials.clear();
  }

  private materialFor(kind: TileKind, color: Color3): StandardMaterial {
    let material = this.materials.get(kind);
    if (!material) {
      material = new StandardMaterial(`mat_${kind}`, this.scene);
      material.diffuseColor = color;
      material.specularColor = new Color3(0.06, 0.06, 0.08);
      this.materials.set(kind, material);
    }
    return material;
  }
}
